import {
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassCardTitle,
} from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Settings, Link2, KeyRound } from "lucide-react";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { buildAppConfigActions } from "@/components/actions";
import { AppConfigSnapshot } from "@/services/appConfigApi";
import { useAppConfigTags } from "@/hooks/useAppConfigurations";
import { TagIcon } from "@/components/tags/TagIcon";
import { InteractiveTitle } from "@/components/ui/interactive-title";

interface AppConfigCardProps {
  config: AppConfigSnapshot;
  appName: string;
  iconUrl?: string;
  linkedToCatalog: boolean;
  onView: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const formatDate = (iso?: string) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "—";
  }
};

export const AppConfigCard = ({
  config,
  appName,
  iconUrl,
  linkedToCatalog,
  onView,
  onEdit,
  onDuplicate,
  onDelete,
}: AppConfigCardProps) => {
  const { data: tags = [] } = useAppConfigTags(config.policyId);
  const keyCount = config.keys.length;

  return (
    <GlassCard className="hover:border-primary/40 transition-colors">
      <GlassCardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            {iconUrl ? (
              <img
                src={iconUrl}
                alt=""
                className="h-9 w-9 rounded-md border shrink-0"
              />
            ) : (
              <Settings className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            )}
            <div className="min-w-0">
              <GlassCardTitle className="text-base">
                <InteractiveTitle onClick={onView} className="block w-full text-base font-semibold">
                  {config.configName}
                </InteractiveTitle>
              </GlassCardTitle>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {appName}
              </p>
              <p className="text-xs font-mono text-muted-foreground truncate mt-0.5">
                {config.bundleId || "—"}
              </p>
            </div>
          </div>
          <ActionsDropdown
            actions={buildAppConfigActions({
              surface: "list",
              onView,
              onEdit,
              onDuplicate,
              onDelete,
            })}
          />
        </div>
      </GlassCardHeader>

      <GlassCardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className="text-xs gap-1">
            <KeyRound className="h-3 w-3" />
            {keyCount} key{keyCount !== 1 ? "s" : ""}
          </Badge>
          {linkedToCatalog ? (
            <Badge variant="secondary" className="text-xs gap-1">
              <Link2 className="h-3 w-3" /> Linked to catalog
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs">External app</Badge>
          )}
        </div>

        {tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {tags.slice(0, 4).map((tag) => (
              <Badge
                key={tag.id}
                variant="secondary"
                className="gap-1 text-[10px] px-1.5 py-0"
                style={{
                  backgroundColor: `${tag.color}20`,
                  borderColor: tag.color,
                  color: tag.color,
                }}
              >
                <TagIcon iconName={tag.icon} color={tag.color} size={8} />
                {tag.name}
              </Badge>
            ))}
            {tags.length > 4 && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                +{tags.length - 4}
              </Badge>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Not deployed — add tags to push this configuration.
          </p>
        )}

        <p className="text-xs text-muted-foreground">
          Updated {formatDate(config.updatedAt)}
        </p>
      </GlassCardContent>
    </GlassCard>
  );
};
